/**
 * Master Game Catalog
 *
 * Single source of truth for all available casino games.
 * Each game has a unique `gameKey` that matches what the backend
 * returns in the `allowedGames` array from /auth/user-info.
 *
 * The `gameKey` is also used for:
 *  - Odds polling: Diamond Casino /api/tunnel/casino/odds/{diamondCode}
 *  - Redux state: state.gameData.{gameKey}Odds
 *  - Route guarding: only render route if gameKey is in allowedGames
 *
 * `service` and `port` map to the backend microservice that handles
 * bet placement for each game (still used by casino bet APIs).
 *
 * `grandExchId` is the type prefix used for GrandExch proxy video URLs
 * (/casino/Video/<id>). Kept as fallback.
 *
 * `diamondCode` is the gameCode used by the Diamond Casino provider
 * for odds, results, and streaming APIs. null = not available in Diamond Casino.
 */

import { GRANDEXCH_URL } from "./constants";
import { getActiveGames } from "../service/liveTable";
import { getGameCatalog } from "../service/liveTable";

export const GAME_CATALOG = [
  {
    gameKey: "dragonTiger20",
    gmid: "dt20",
    name: "Dragon Tiger 20-20 1 Day",
    image: "/images/live-casino/dragon-tiger-20.png",
    link: "/live-casino/dragon-tiger/20-20",
    group: "dragon-tiger",
    service: "s2",
    port: 9003,
    grandExchId: "dt20",
    diamondCode: "DRAGON_TIGER_20",
  },
  {
    gameKey: "dragonTiger202",
    gmid: "dt202",
    name: "Dragon Tiger 20-20 2 Day",
    image: "/images/live-casino/dragon-tiger-20-2.png",
    link: "/live-casino/dragon-tiger/20-20/2",
    group: "dragon-tiger",
    service: "s2",
    port: 9003,
    grandExchId: "dt202",
    diamondCode: "DRAGON_TIGER_20_2",
  },
  {
    gameKey: "dragonTigerOneDay",
    gmid: "dt6",
    name: "Dragon Tiger 1 Day",
    image: "/images/live-casino/dragon-tiger-day-1.png",
    link: "/live-casino/dragon-tiger/1-day",
    group: "dragon-tiger",
    service: "s2",
    port: 9003,
    grandExchId: "dt6",
    diamondCode: "DRAGON_TIGER_6",
  },
  {
    gameKey: "dragonTigerLion",
    gmid: "dtl20",
    name: "Dragon Tiger Lion",
    image: "/images/live-casino/dragon-tiger-lion.png",
    link: "/live-casino/dragon-tiger/dtl",
    group: "dragon-tiger",
    service: "s2",
    port: 9003,
    grandExchId: "dtl20",
    diamondCode: "DRAGON_TIGER_LION_20",
  },
  {
    gameKey: "teenPatti20",
    gmid: "teen20",
    name: "Teen Patti 20-20",
    image: "/images/live-casino/teen-patti.png",
    link: "/live-casino/teenpatti/20-20",
    group: "teenpatti",
    service: "s1",
    port: 9002,
    grandExchId: "teen20",
    diamondCode: "TEEN_20",
  },
  {
    gameKey: "teen9",
    gmid: "teen9",
    name: "Teen Patti Test",
    image: "/images/live-casino/teen-patti-test.png",
    link: "/live-casino/teenpatti/test",
    group: "teenpatti",
    service: "s1",
    port: 9002,
    grandExchId: "teen9",
    diamondCode: "TEEN_9",
  },
  {
    gameKey: "teen8",
    gmid: "teen8",
    name: "Teen Patti Open",
    image: "/images/live-casino/teen-patti-open.png",
    link: "/live-casino/teenpatti/open",
    group: "teenpatti",
    service: "s1",
    port: 9002,
    grandExchId: "teen8",
    diamondCode: "TEEN_8",
  },
  {
    gameKey: "teen",
    gmid: "teen",
    name: "Teen Patti 1 Day",
    image: "/images/live-casino/teen-patti-day-1.png",
    link: "/live-casino/teenpatti/1-day",
    group: "teenpatti",
    service: "s1",
    port: 9002,
    grandExchId: "teen",
    diamondCode: "TEEN",
  },
  {
    gameKey: "casinoQueen",
    gmid: "queen",
    name: "Casino Queen",
    image: "queen.jpg",
    link: "/live-casino/cn/queen",
    group: "casino",
    service: "s7",
    port: 9008,
    grandExchId: "queen",
    diamondCode: null,
  },
  {
    gameKey: "card32",
    gmid: "card32",
    name: "32 Cards A",
    image: "card32.jpg",
    link: "/live-casino/card-32/a",
    group: "32-cards",
    service: "s3",
    port: 9004,
    grandExchId: "card32",
    diamondCode: "CARD_32",
  },
  {
    gameKey: "card32eu",
    gmid: "card32eu",
    name: "32 Cards B",
    image: "card32eu.jpg",
    link: "/live-casino/card-32/b",
    group: "32-cards",
    service: "s3",
    port: 9004,
    grandExchId: "card32eu",
    diamondCode: "CARD32EU",
  },
  {
    gameKey: "poker20",
    gmid: "poker20",
    name: "Poker 20-20",
    image: "poker20.jpg",
    link: "/live-casino/poker/20-20",
    group: "poker",
    service: "s5",
    port: 9006,
    grandExchId: "poker20",
    diamondCode: "POKER_20",
  },
  {
    gameKey: "poker9",
    gmid: "poker9",
    name: "Poker 6 Players",
    image: "poker6.jpg",
    link: "/live-casino/poker/poker-6-player",
    group: "poker",
    service: "s5",
    port: 9006,
    grandExchId: "poker9",
    diamondCode: "POKER_9",
  },
  {
    gameKey: "poker1Day",
    gmid: "poker1day",
    name: "Poker 1 Day",
    image: "poker.jpg",
    link: "/live-casino/poker",
    group: "poker",
    service: "s5",
    port: 9006,
    grandExchId: null,
    diamondCode: "POKER_1_DAY",
  },
  {
    gameKey: "lucky7",
    gmid: "lucky7",
    name: "Lucky 7-A",
    image: "lucky7.jpg",
    link: "/live-casino/lucky7/a",
    group: "lucky",
    service: "s3",
    port: 9004,
    grandExchId: "lucky7",
    diamondCode: "LUCKY7",
  },
  {
    gameKey: "lucky7eu",
    gmid: "lucky7eu",
    name: "Lucky 7-B",
    image: "lucky7eu.jpg",
    link: "/live-casino/lucky7/b",
    group: "lucky",
    service: "s3",
    port: 9004,
    grandExchId: "lucky7eu",
    diamondCode: "LUCKY7EU",
  },
  {
    gameKey: "andarBahar20",
    gmid: "ab20",
    name: "Andar Bahar",
    image: "ab20.jpg",
    link: "/live-casino/andar-bahar",
    group: "andar-bahar",
    service: "s4",
    port: 9005,
    grandExchId: "ab20",
    diamondCode: "AB_20",
  },
  {
    gameKey: "abj",
    gmid: "abj",
    name: "Andar Bahar 2",
    image: "andar-bahar2.jpg",
    link: "/live-casino/andar-bahar/2",
    group: "andar-bahar",
    service: "s4",
    port: 9005,
    grandExchId: "abj",
    diamondCode: "ABJ",
  },
  {
    gameKey: "aaa",
    gmid: "aaa",
    name: "Amar Akbar Anthony",
    image: "aaa.jpg",
    link: "/live-casino/andar-bahar/aaa",
    group: "andar-bahar",
    service: "s4",
    port: 9005,
    grandExchId: "aaa",
    diamondCode: "AAA",
  },
  {
    gameKey: "bollywoodTable",
    gmid: "btable",
    name: "Bollywood Casino",
    image: "btable.jpg",
    link: "/live-casino/cn/bollywood-table",
    group: "bollywood",
    service: "s7",
    port: 9008,
    grandExchId: "btable",
    diamondCode: "BOLLYWOOD_TABLE",
  },
  {
    gameKey: "casinoWar",
    gmid: "war",
    name: "Casino War",
    image: "war.jpg",
    link: "/live-casino/cn/war",
    group: "casino",
    service: "s7",
    port: 9008,
    grandExchId: "war",
    diamondCode: "CASINO_WAR",
  },
  {
    gameKey: "casinoMeter",
    gmid: "cmeter",
    name: "Casino Meter",
    image: "cmeter.jpg",
    link: "/live-casino/cn/meter",
    group: "casino",
    service: "s7",
    port: 9008,
    grandExchId: "cmeter",
    diamondCode: null,
  },
  {
    gameKey: "race20",
    gmid: "race20",
    name: "Race 20",
    image: "race20.jpg",
    link: "/live-casino/race/20-20",
    group: "race",
    service: "s4",
    port: 9005,
    grandExchId: "race20",
    diamondCode: "RACE20",
  },
  {
    gameKey: "worli",
    gmid: "worli",
    name: "Worli Matka",
    image: "worli matka.jpg",
    link: "/live-casino/worli",
    group: "worli",
    service: "s6",
    port: 9007,
    grandExchId: "worli",
    diamondCode: "WORLI2",
  },
  {
    gameKey: "worli2",
    gmid: "worli",
    name: "Instant Worli",
    image: "worli2.jpg",
    link: "/live-casino/worli/2",
    group: "worli",
    service: "s6",
    port: 9007,
    grandExchId: "worli3",
    diamondCode: "WORLI2",
  },
  {
    gameKey: "3cardJudgement",
    gmid: "3cardsjudgement",
    name: "3 Cards Judgement",
    image: "3 card judgement.jpg",
    link: "/live-casino/3cardsjudgement",
    group: "casino",
    service: "s4",
    port: 9005,
    grandExchId: "3cardJudgement",
    diamondCode: "THREE_CARD",
  },
  {
    gameKey: "cricketV3",
    gmid: "cricketv3",
    name: "5-5 Cricket",
    image: "cricketv3.jpg",
    link: "/live-casino/cricket/five",
    group: "sports-casino",
    service: "s6",
    port: 9007,
    grandExchId: "cricketv3",
    diamondCode: "CRICKET_V3",
  },
  {
    gameKey: "cmatch20",
    gmid: "cmatch20",
    name: "Cricket 20-20",
    image: "cmatch20.jpg",
    link: "/live-casino/cricket/20-20",
    group: "sports-casino",
    service: "s6",
    port: 9007,
    grandExchId: "cmatch20",
    diamondCode: "CRICKET_MATCH_20",
  },
  {
    gameKey: "superOver",
    gmid: "superover",
    name: "Super Over",
    image: "superover.jpg",
    link: "/live-casino/cricket/super-over",
    group: "sports-casino",
    service: "s6",
    port: 9007,
    grandExchId: "superover",
    diamondCode: "SUPEROVER",
  },
  {
    gameKey: "baccarat",
    gmid: "baccarat",
    name: "Baccarat",
    image: "baccarat.jpg",
    link: "/live-casino/baccarat",
    group: "baccarat",
    service: "s5",
    port: 9006,
    grandExchId: "baccarat",
    diamondCode: "BACCARAT",
  },
  {
    gameKey: "baccarat2",
    gmid: "baccarat2",
    name: "Baccarat 2",
    image: "baccarat2.jpg",
    link: "/live-casino/baccarat/2",
    group: "baccarat",
    service: "s5",
    port: 9006,
    grandExchId: "baccarat2",
    diamondCode: "BACCARAT2",
  },
];

/**
 * Module-level cache for Diamond Casino stream URLs.
 * Populated when the game list API is fetched (in CasinoList).
 * Map of gameKey -> streamURL
 *
 * Stream URLs point directly to the provider (e.g. https://apis.professorji.in/api/casino-streaming/3030).
 * The provider must whitelist the frontend domain so the user's browser can load them directly.
 */
const diamondStreamCache = {};

export const setDiamondStreamUrl = (gameKey, streamUrl) => {
  diamondStreamCache[gameKey] = streamUrl;
};

export const getDiamondStreamUrl = (gameKey) => {
  return diamondStreamCache[gameKey] || null;
};

let preloadDone = false;
export const preloadDiamondStreamUrls = async () => {
  if (preloadDone) return;
  preloadDone = true;
  try {
    const response = await getActiveGames();
    // Live Table API: { statusCode, message, data: [...games] }
    const apiGames = response?.data?.data || response?.data || [];
    if (!Array.isArray(apiGames)) return;
    for (const game of apiGames) {
      if (!game.gameCode || !game.streamURL) continue;
      const gameKey = getGameKeyByDiamondCode(game.gameCode);
      if (gameKey) {
        // Proxy stream through our server to avoid provider IP whitelist blocking browsers.
        // Convert "https://apis.professorji.in/api/casino-streaming/3035" → "/casino-streaming/3035"
        try {
          const streamPath = new URL(game.streamURL).pathname.replace(/^\/api/, "");
          diamondStreamCache[gameKey] = streamPath;
        } catch {
          diamondStreamCache[gameKey] = game.streamURL;
        }
      }
    }
  } catch {
    preloadDone = false;
  }
};

/**
 * Module-level cache for dynamic game codes fetched from the API.
 * Map of gameKey -> gameCode (Diamond Casino code).
 * Checked first by getDiamondCodeByGameKey() before falling back to hardcoded values.
 */
const dynamicGameCodeCache = {};

let catalogLoadDone = false;
export const loadGameCatalog = async () => {
  if (catalogLoadDone) return;
  catalogLoadDone = true;
  try {
    const response = await getGameCatalog();
    const data = response?.data;
    const games = data?.response || data?.data || [];
    if (!Array.isArray(games) || games.length === 0) {
      catalogLoadDone = false;
      return;
    }
    for (const game of games) {
      if (game.gameKey && game.gameCode) {
        dynamicGameCodeCache[game.gameKey] = game.gameCode;
      }
    }
  } catch {
    catalogLoadDone = false;
  }
};

/**
 * Returns the filtered game list for an operator.
 * If allowedGames is empty/null, returns ALL games (backwards compatible).
 */
export const getOperatorGames = (allowedGames) => {
  if (!allowedGames || allowedGames.length === 0) {
    return GAME_CATALOG;
  }
  const allowedSet = new Set(allowedGames);
  return GAME_CATALOG.filter((game) => allowedSet.has(game.gameKey));
};

/**
 * Check if a specific game is allowed for the operator.
 */
export const isGameAllowed = (gameKey, allowedGames) => {
  if (!allowedGames || allowedGames.length === 0) return true;
  return allowedGames.includes(gameKey);
};

/**
 * Returns all unique gameKeys (for reference / backend seeding).
 */
export const getAllGameKeys = () => GAME_CATALOG.map((g) => g.gameKey);

/**
 * Find a game entry by its route path.
 * Matches the longest prefix -- e.g. "/live-casino/dragon-tiger/20-20/2"
 * matches the DT202 entry, not the DT20 entry.
 */
export const getGameByPath = (pathname) => {
  let bestMatch = null;
  for (const game of GAME_CATALOG) {
    if (pathname.startsWith(game.link)) {
      if (!bestMatch || game.link.length > bestMatch.link.length) {
        bestMatch = game;
      }
    }
  }
  return bestMatch;
};

/**
 * Find a game entry by its gameKey.
 */
export const getGameByKey = (gameKey) =>
  GAME_CATALOG.find((g) => g.gameKey === gameKey) || null;

/**
 * Build the GrandExch proxy video URL for a given gameKey.
 * Returns null if the game has no grandExchId mapped yet.
 */
export const getGrandExchVideoUrl = (gameKey) => {
  const game = getGameByKey(gameKey);
  if (!game?.grandExchId) return null;
  return `${GRANDEXCH_URL}/casino/Video/${game.grandExchId}`;
};

/**
 * Get the gmid (live-table topic ID) for a given gameKey.
 */
export const getGmidByGameKey = (gameKey) => {
  const game = getGameByKey(gameKey);
  return game?.gmid || null;
};

/**
 * Get the gameKey for a given gmid.
 */
export const getGameKeyByGmid = (gmid) => {
  const game = GAME_CATALOG.find((g) => g.gmid === gmid);
  return game?.gameKey || null;
};

/**
 * Look up a game entry by its Diamond Casino gameCode.
 * Returns the gameKey string or null if not found.
 */
export const getGameKeyByDiamondCode = (diamondCode) => {
  if (!diamondCode) return null;
  const game = GAME_CATALOG.find((g) => g.diamondCode === diamondCode);
  return game?.gameKey || null;
};

/**
 * Get the gameKey for a given route pathname.
 * Matches against the `link` field of each catalog entry.
 */
export const getGameKeyByLink = (pathname) => {
  if (!pathname) return null;
  const game = GAME_CATALOG.find((g) => g.link === pathname);
  return game?.gameKey || null;
};

/**
 * Get the Diamond Casino gameCode for a given gameKey.
 * Checks the dynamic API cache first, falls back to hardcoded GAME_CATALOG values.
 * Returns null if the game has no Diamond Casino mapping.
 */
export const getDiamondCodeByGameKey = (gameKey) => {
  if (dynamicGameCodeCache[gameKey]) return dynamicGameCodeCache[gameKey];
  const game = getGameByKey(gameKey);
  return game?.diamondCode || null;
};
